import { useState, useMemo, MouseEvent } from 'react'
import { Box, Button, Paper, Typography, IconButton, Popover, Radio, RadioGroup, FormControlLabel } from '@mui/material'
import { ChevronLeft, ChevronRight, DateRange } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'
import { DaylightDateFormat } from '@/constants/DaylightDateFormat'

interface DateRangePickerProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  onDateChange: (startDate: Dayjs | null, endDate: Dayjs | null) => void
  maxDaysRange?: number // Maximum allowed date range in days (default: 89)
  showLastYearOption?: boolean // Show/hide "Last Year" quick select option (default: true)
}

export const DateRangePicker = ({
  startDate,
  endDate,
  onDateChange,
  maxDaysRange = 89,
  showLastYearOption = true,
}: DateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [currentMonth, setCurrentMonth] = useState(dayjs())
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(startDate)
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(endDate)
  const [isSelectingEnd, setIsSelectingEnd] = useState(false)
  const [selectedOption, setSelectedOption] = useState<string>('custom')

  const open = Boolean(anchorEl)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setTempStartDate(startDate)
    setTempEndDate(endDate)
    setIsSelectingEnd(false)
  }

  const handleClose = () => {
    setAnchorEl(null)
    // Reset temp dates to current selection on close without applying
    setTempStartDate(startDate)
    setTempEndDate(endDate)
  }

  const handleApply = () => {
    // Apply the selection when OK button is clicked
    onDateChange(tempStartDate, tempEndDate)
    handleClose()
  }

  const handleQuickSelect = (type: string) => {
    setSelectedOption(type)

    if (type === 'custom') {
      // Don't set dates for custom option
      return
    }

    const today = dayjs()
    const maxDaysAgo = today.subtract(maxDaysRange, 'days')
    let start: Dayjs
    let end: Dayjs = today

    switch (type) {
      case 'last24hrs':
        start = today.subtract(1, 'day')
        end = today
        break
      case 'last7days':
        start = today.subtract(6, 'days')
        end = today
        break
      case 'lastMonth':
        start = today.subtract(1, 'month')
        end = today
        break
      case 'lastYear':
        // Limit to configured max days range instead of 1 year
        start = maxDaysAgo
        end = today
        break
      default:
        return
    }

    setTempStartDate(start)
    setTempEndDate(end)
    // Don't auto-apply, wait for OK button click
  }

  const handleReset = () => {
    setTempStartDate(null)
    setTempEndDate(null)
    setSelectedOption('custom')
    // Don't auto-apply, wait for OK button click
  }

  const handleDateClick = (date: Dayjs) => {
    setSelectedOption('custom') // Set to custom when manually selecting dates

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Start new selection
      setTempStartDate(date)
      setTempEndDate(null)
      setIsSelectingEnd(true)
    } else if (isSelectingEnd) {
      // Select end date
      let finalStart = tempStartDate
      let finalEnd = date

      if (date.isBefore(tempStartDate)) {
        // If end date is before start date, swap them
        finalStart = date
        finalEnd = tempStartDate
      }

      setTempEndDate(finalEnd)
      setTempStartDate(finalStart)
      setIsSelectingEnd(false)

      // Don't auto-apply, wait for OK button click
    }
  }

  const isDateInRange = (date: Dayjs) => {
    if (!tempStartDate || !tempEndDate) return false
    return date.isAfter(tempStartDate) && date.isBefore(tempEndDate)
  }

  const isDateSelected = (date: Dayjs) => {
    if (!tempStartDate) return false
    if (tempStartDate.isSame(date, 'day')) return true
    if (tempEndDate && tempEndDate.isSame(date, 'day')) return true
    return false
  }

  const formatDateRange = () => {
    if (!startDate || !endDate) return ''
    return `${startDate.format(DaylightDateFormat.DATE_SHORT_SLASH)} - ${endDate.format(DaylightDateFormat.DATE_SHORT_SLASH)}`
  }

  // Generate calendar days for current month
  const generateCalendarDays = (month: Dayjs) => {
    const startOfMonth = month.startOf('month')
    const startDay = startOfMonth.day() // 0 = Sunday
    const daysInMonth = month.daysInMonth()

    const days: (Dayjs | null)[] = []

    // Add empty cells for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(null)
    }

    // Add all days in month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(month.date(i))
    }

    return days
  }

  const currentMonthDays = useMemo(() => generateCalendarDays(currentMonth), [currentMonth])

  const previousMonth = currentMonth.subtract(1, 'month')
  const previousMonthDays = useMemo(() => generateCalendarDays(previousMonth), [previousMonth])

  const renderCalendar = (month: Dayjs, days: (Dayjs | null)[]) => {
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    const today = dayjs()
    const maxDaysAgo = today.subtract(maxDaysRange + 1, 'days')

    // Check if we can navigate backward (previous month has at least one selectable date)
    const canNavigateBack = currentMonth.subtract(1, 'month').endOf('month').isAfter(maxDaysAgo, 'day')

    // Check if we can navigate forward (next month is not in the future)
    const nextMonthStart = currentMonth.add(1, 'month').startOf('month')
    const canNavigateForward = nextMonthStart.isBefore(today, 'day') || nextMonthStart.isSame(today, 'day')

    return (
      <Box sx={{ width: 280 }}>
        {/* Month Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
            gap: 2,
          }}
        >
          {month.isSame(previousMonth, 'month') && (
            <>
              {canNavigateBack ? (
                <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                  sx={{ color: '#666' }}
                >
                  <ChevronLeft />
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} /> // Placeholder to maintain spacing
              )}
            </>
          )}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              color: '#333',
              minWidth: 140,
              textAlign: 'center',
            }}
          >
            {month.format('MMMM YYYY')}
          </Typography>
          {month.isSame(currentMonth, 'month') && (
            <>
              {canNavigateForward ? (
                <IconButton
                  size="small"
                  onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                  sx={{ color: '#666' }}
                >
                  <ChevronRight />
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} /> // Placeholder to maintain spacing
              )}
            </>
          )}
        </Box>

        {/* Week Day Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            mb: 1,
          }}
        >
          {weekDays.map((day, index) => (
            <Box
              key={index}
              sx={{
                textAlign: 'center',
                fontSize: '12px',
                fontWeight: 600,
                color: '#999',
                py: 0.5,
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Calendar Days */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
          }}
        >
          {days.map((day, index) => {
            if (!day) {
              return <Box key={`empty-${index}`} sx={{ height: 36 }} />
            }

            const isSelected = isDateSelected(day)
            const isInRange = isDateInRange(day)
            const isToday = day.isSame(dayjs(), 'day')
            const isFuture = day.isAfter(dayjs(), 'day')
            // Disable dates older than configured max days range from today
            const maxDaysAgo = dayjs().subtract(maxDaysRange, 'days')
            const isTooOld = day.isBefore(maxDaysAgo, 'day')
            const isDisabled = isFuture || isTooOld

            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                onClick={() => !isDisabled && handleDateClick(day)}
                sx={{
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  fontSize: '14px',
                  fontWeight: isToday ? 600 : 400,
                  color: isDisabled ? '#ccc' : isSelected ? '#fff' : isInRange ? '#1976d2' : '#333',
                  backgroundColor: isDisabled
                    ? 'transparent'
                    : isSelected
                      ? '#1976d2'
                      : isInRange
                        ? '#e3f2fd'
                        : 'transparent',
                  borderRadius: '50%',
                  opacity: isDisabled ? 0.4 : 1,
                  '&:hover': {
                    backgroundColor: isDisabled
                      ? 'transparent'
                      : isSelected
                        ? '#1565c0'
                        : isInRange
                          ? '#bbdefb'
                          : '#f5f5f5',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {day.date()}
              </Box>
            )
          })}
        </Box>
      </Box>
    )
  }

  return (
    <>
      {/* Trigger Button */}
      <Box
        onClick={(e) => handleClick(e as unknown as MouseEvent<HTMLButtonElement>)}
        sx={{
          position: 'relative',
          minWidth: 220,
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            px: 1.75,
            py: 1.0,
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 40,
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
            },
          }}
        >
          <Typography
            sx={{
              color: formatDateRange() ? '#000000' : '#999',
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '24px',
              letterSpacing: '0.45px',
            }}
          >
            {formatDateRange() || 'Select date range'}
          </Typography>
          <DateRange sx={{ fontSize: 20, color: '#666' }} />
        </Box>

        {/* Label on border */}
        <Box
          component="label"
          sx={{
            position: 'absolute',
            top: -8,
            left: 8,
            px: 0.5,
            backgroundColor: '#fff',
            fontSize: '12px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            pointerEvents: 'none',
          }}
        >
          Date Range
        </Box>
      </Box>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          mt: 1,
        }}
      >
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Quick Select Radio Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 150,
                pr: 2,
              }}
            >
              <RadioGroup
                value={selectedOption}
                onChange={(e) => handleQuickSelect(e.target.value)}
                sx={{ gap: '10px' }}
              >
                <FormControlLabel
                  value="last24hrs"
                  control={<Radio size="small" sx={{ py: 0.5, color: '#026AB7' }} />}
                  label="Last 24 hrs"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      color: '#333',
                      fontWeight: 600,
                      letterSpacing: 0.8,
                    },
                  }}
                />
                <FormControlLabel
                  value="last7days"
                  control={<Radio size="small" sx={{ py: 0.5, color: '#026AB7' }} />}
                  label="Last 7 days"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      color: '#333',
                      fontWeight: 600,
                      letterSpacing: 0.8,
                    },
                  }}
                />
                <FormControlLabel
                  value="lastMonth"
                  control={<Radio size="small" sx={{ py: 0.5, color: '#026AB7' }} />}
                  label="Last month"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      color: '#333',
                      fontWeight: 600,
                      letterSpacing: 0.8,
                    },
                  }}
                />
                {showLastYearOption && (
                  <FormControlLabel
                    value="lastYear"
                    control={<Radio size="small" sx={{ py: 0.5, color: '#026AB7' }} />}
                    label="Last year"
                    sx={{
                      m: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '16px',
                        color: '#333',
                        fontWeight: 600,
                        letterSpacing: 0.8,
                      },
                    }}
                  />
                )}
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" sx={{ py: 0.5, color: '#026AB7' }} />}
                  label="Custom"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '16px',
                      color: '#333',
                      fontWeight: 600,
                      letterSpacing: 0.8,
                    },
                  }}
                />
              </RadioGroup>

              {/* Reset Button */}
              <Button
                onClick={handleReset}
                size="small"
                sx={{
                  textTransform: 'none',
                  justifyContent: 'flex-end',
                  color: '#1976d2',
                  fontSize: '14px',
                  fontWeight: 500,
                  mt: 1,
                  px: 1,
                  py: 0.5,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Reset
              </Button>
            </Box>

            {/* Divider */}
            <Box sx={{ width: 1, backgroundColor: '#e0e0e0' }} />

            {/* Calendars */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              {renderCalendar(previousMonth, previousMonthDays)}
              {renderCalendar(currentMonth, currentMonthDays)}
            </Box>
          </Box>

          {/* OK Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={handleApply}
              disabled={!tempStartDate || !tempEndDate}
              sx={{
                textTransform: 'none',
                backgroundColor: '#1976d2',
                color: '#fff',
                px: 2,
                py: 0.5,
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999',
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  )
}
